import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateWasteProducerMutation, useUpdateWasteProducerMutation } from '../../../../hooks/mutations/useWasteProducerMutation';
import { WasteProducer } from '../../../../services/apiSLTP/sites/wasteProducers';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';
import { MuiTelInput } from 'mui-tel-input';

interface ModalProps {
    wasteProducer: WasteProducer | null;
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateWasteProducerModal: React.FC<ModalProps> = ({ wasteProducer, showEditModal, setShowEditModal, isEditMode = true, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(wasteProducer?.designation || '');
    const [siret, setSiret] = useState(wasteProducer?.siret || '');
    const [contactLastName, setContactLastName] = useState(wasteProducer?.contactLastName || '');
    const [contactFirstName, setContactFirstName] = useState(wasteProducer?.contactFirstName || '');
    const [contactEmail, setContactEmail] = useState(wasteProducer?.contactEmail || '');
    const [contactPhone, setContactPhone] = useState(wasteProducer?.contactPhone || '');
    const [crudStatusEnumState, setCrudStatusState] = useState(wasteProducer?.crudStatus || crudStatusEnum.OK);

    const { mutate: mutateWasteProducer, isLoading: isLoadingUpdate } = useUpdateWasteProducerMutation({
        onSuccess: () => {
            refetch();
            setShowEditModal(false);
        },
        onError: (error) => {
            console.error('Error updating waste producer:', error.message);
        },
    });

    const { mutate: addWasteProducer, isLoading: isLoadingCreate } = useCreateWasteProducerMutation({
        onSuccess: () => {
            refetch();
            setShowEditModal(false);
        },
        onError: (error) => {
            console.error('Error adding waste producer:', error.message);
        },
    });

    const handleActiveChange = (isActive: boolean) => {
        setCrudStatusState(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    useEffect(() => {
        if (wasteProducer) {
            setDesignation(wasteProducer.designation);
            setSiret(wasteProducer.siret);
            setContactLastName(wasteProducer.contactLastName || '');
            setContactFirstName(wasteProducer.contactFirstName || '');
            setContactEmail(wasteProducer.contactEmail || '');
            setContactPhone(wasteProducer.contactPhone || '');
            setCrudStatusState(wasteProducer.crudStatus);
        }
    }, [wasteProducer]);

    return (
        <ModalBody
            title={t('wasteProducer')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!designation.length || !siret.length}
            submitLoading={isLoadingUpdate || isLoadingCreate}
            onSubmit={() => {
                if (isEditMode && wasteProducer) {
                    mutateWasteProducer({
                        id: wasteProducer._id,
                        data: {
                            designation,
                            siret: siret,
                            contactLastName: contactLastName,
                            contactFirstName: contactFirstName,
                            contactEmail: contactEmail,
                            contactPhone: contactPhone,
                            crudStatus: crudStatusEnumState,
                        },
                    });
                } else {
                    addWasteProducer({
                        designation,
                        siret: siret,
                        contactLastName: contactLastName,
                        contactFirstName: contactFirstName,
                        contactEmail: contactEmail,
                        contactPhone: contactPhone,
                        crudStatus: crudStatusEnumState,
                    });
                }
            }}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={(text) => setDesignation(text)} required />
            <CustomTextInput value={siret} label={t('siret')} onChange={(text) => setSiret(text)} required />
            <ToggleLine
                checked={crudStatusEnumState === crudStatusEnum.OK}
                onChange={handleActiveChange}
                title={capitalizeFirstLetter(t('active'))}
            />
            <CustomTextInput value={contactLastName} label={t('contactLastName')} onChange={(text) => setContactLastName(text)} />
            <CustomTextInput value={contactFirstName} label={t('contactFirstName')} onChange={(text) => setContactFirstName(text)} />
            <CustomTextInput value={contactEmail} label={t('contactEmail')} onChange={(text) => setContactEmail(text)} type="email" />
            <div className="pad-l-5px pad-r-5px">
                <MuiTelInput
                    value={contactPhone}
                    onChange={setContactPhone}
                    label={capitalizeFirstLetter(t('mobile'))}
                    defaultCountry="FR"
                    preferredCountries={['FR', 'BE', 'CH', 'LU']}
                    fullWidth
                    size="small"
                    langOfCountryName={t('locale')}
                />
            </div>
        </ModalBody>
    );
};

export default UpdateWasteProducerModal;
